/**
 * Logistics Landing Page Content
 * All text and images specific to the logistics industry landing page
 */
import { CheckCircleOutline, CreditCardOff, LockOutlined } from '@mui/icons-material';
// ===== APP BAR =====
export const appBar = {
    navLinks: [
        { label: 'Pricing', href: '#pricing', isAnchor: true },
        { label: 'FAQ', href: '#faq', isAnchor: true },
    ],
    showUrgentHelp: true,
    phoneNumber: 'tel:+12063548337',
    ctaText: 'Start Free Trial',
    ctaHref: '/onboarding?industry=logistics',
    showTrustpilot: true,
};
// ===== HERO SECTION =====
export const hero = {
    eyebrow: 'BUILT AND TRUSTED BY SMALL BUSINESS MANAGERS',
    headline: 'Turn 500 resumes into',
    headlineHighlight: '5 scheduled interviews',
    subheadline: 'Say goodbye to endless resume screening and dead-end calls. We text applicants to verify the hard facts — shift availability, commute distance, and certifications. You review a clean list of matches, hit approve, and wake up to booked interviews.',
    primaryCtaText: 'Score a resume in 30s →',
    primaryCtaHref: '/onboarding?industry=logistics',
    secondaryCtaText: 'Watch Demo',
    secondaryCtaHref: '#how-it-works',
    badges: [
        { icon: CheckCircleOutline, text: 'No ATS setup' },
        { icon: CreditCardOff, text: 'No card required' },
        { icon: LockOutlined, text: 'Data safe' },
    ],
    heroImageAlt: 'AI Resume Screening for Logistics',
    showTrustpilot: true,
};
// ===== COMMON QUESTIONS SECTION =====
export const commonQuestions = {
    label: 'WHY WAREHOUSE MANAGERS CHOOSE US',
    title: 'Common Questions From Hiring Managers Like You',
    subtitle: 'We filter the incoming resumes and text candidates to verify their commute and pay expectations.',
    questions: [
        {
            question: 'Does this work for frontline roles?',
            answer: 'Yes, absolutely. Stanor perfectly handles high-volume, hourly, and frontline roles. It was designed specifically for them.',
        },
        {
            question: "I'm not technical\u2014is this complicated?",
            answer: 'Not at all. Stanor automates the busywork like screening, messaging, scheduling. You focus on picking the best people with just a few clicks.',
        },
        {
            question: 'What if the AI gets it wrong?',
            answer: 'You are always in control. The AI works based on the strict criteria. You can review the results anytime, and you make the final hiring decision.',
        },
        {
            question: 'How much does it cost?',
            answer: 'You can start with a free trial. After that, our plans scale with your hiring volume, starting at just $99/month. No long-term contracts. Cancel anytime.',
        },
    ],
};
// ===== TESTIMONIALS SECTION =====
export const testimonials = {
    title: 'What Logistics Managers Are Saying',
    subtitle: 'Real results from warehouse and logistics managers just like you',
    testimonials: [
        {
            name: 'James Martinez',
            role: 'Operations Manager',
            company: 'Regional HVAC',
            rating: 5,
            quote: 'I had 47 resumes for 2 HVAC tech positions and was dreading the weekend of sorting. StanorAI ranked them in literally 2 minutes. I called the top 5, interviewed 3, and hired 2 certified techs within a week.',
        },
        {
            name: 'Linda Torres',
            role: 'Distribution Center Manager',
            company: 'Regional Logistics',
            rating: 5,
            quote: 'We needed forklift operators with OSHA certs fast. StanorAI screened 75 resumes and surfaced the 8 candidates with proper certifications. Saved me an entire day of manual screening.',
        },
        {
            name: 'Roberto Chen',
            role: 'Warehouse Manager',
            company: 'Logistics',
            rating: 5,
            quote: 'I was skeptical about AI screening, but the free trial convinced me. I uploaded 35 warehouse associate resumes. Top 3 candidates were exactly who I would have picked manually—but it took 90 seconds instead of 3 hours.',
        },
    ],
};
// ===== VALUE PROPS SECTION =====
export const valueProps = {
    label: 'WHY WAREHOUSE MANAGERS CHOOSE US',
    title: 'Hire faster without compromising on quality',
    subtitle: "In warehousing, the best candidates don't wait. Here's how Stanor helps you move faster.",
    images: [
        {
            src: '/assets/landing/logistics/why_1.png',
            alt: 'Logistics manager working',
            badge: { type: 'qualified-count', count: 67 },
        },
        {
            src: '/assets/landing/logistics/why_2.png',
            alt: 'Logistics staff reviewing candidates',
            badge: { type: 'notification', name: 'Jared G.' },
        },
    ],
    valueProps: [
        {
            icon: '/assets/landing/logistics/icons/why_1.svg',
            title: 'Zero Phone Screening',
            description: 'AI instantly texts applicants to verify their shifts and commute. Never waste another minute playing phone tag with unqualified leads.',
        },
        {
            icon: '/assets/landing/logistics/icons/why_2.svg',
            title: 'End Candidate Ghosting',
            description: 'Automated SMS reminders keep applicants engaged and ensure your top picks actually show up for their scheduled interviews.',
        },
        {
            icon: '/assets/landing/logistics/icons/why_3.svg',
            title: 'You Keep Total Control',
            description: "Review the AI's pre-screened shortlist and bulk-approve your favorites. You make the final call; we handle the calendar scheduling.",
        },
        {
            icon: '/assets/landing/logistics/icons/why_4.svg',
            title: 'Zero Tech Skills Needed',
            description: 'If you can drag and drop a file, you can use Stanor AI. No training, no IT setup, and no complicated corporate dashboards.',
        },
    ],
};
// ===== HOW IT WORKS SECTION =====
export const howItWorks = {
    label: 'NO TRAINING NEEDED',
    title: 'Built for High-Volume Hourly Hiring',
    subtitle: 'Frontline workers don\'t check emails. They text. See how Stanor\'s SMS-first AI reaches the best candidates instantly and books them automatically.',
    videoSrc: '/assets/landing/shared/how-it-works.mp4',
    videoPoster: '/assets/landing/shared/how-it-works-poster.jpg',
    steps: [
        {
            icon: '/assets/landing/logistics/icons/simple_1.svg',
            title: 'Screens 50 resumes in 2 minutes',
            description: 'Stanor instantly reads, grades, and ranks the best fits based on your exact requirements, saving you hours of manual review.',
        },
        {
            icon: '/assets/landing/logistics/icons/simple_2.svg',
            title: 'Asks custom dealbreaker questions via SMS',
            description: 'Stanor texts your top matches immediately to verify crucial details like shift availability, lifting capacity, so you don\'t have to.',
        },
        {
            icon: '/assets/landing/logistics/icons/simple_3.svg',
            title: 'Handles scheduling and rescheduling',
            description: 'Candidates book directly into your calendar via text. Smart SMS reminders keep them engaged and manage rescheduling, drastically cutting your no-show rate.',
        },
    ],
};
// ===== PAIN POINTS SECTION =====
export const painPoints = {
    label: 'SOLVING YOUR PAIN POINTS',
    title: 'Stop Losing Great Staff to Slow Hiring',
    subtitle: 'In logistics, the best candidates don\'t wait. Here\'s how StanorAI helps you move faster.',
    manualCard: {
        title: 'Manual Screening',
        timeValue: '6+ hours',
        items: ['Manual Applicant Screening', 'Playing "Phone Tag"', 'High "No-Show" Rates'],
    },
    stanorCard: {
        title: 'With Stanor',
        timeValue: '5 minutes',
        items: ['Instant scoring', 'SMS-first screening', 'Zero-Touch Scheduling', 'Automated Reminders'],
        isHighlighted: true,
        highlightMessage: 'Save 6+ hours per hire.',
    },
};
// ===== ROLES SECTION =====
export const roles = {
    label: 'TRUSTED BY LOGISTICS COMPANIES',
    title: 'Built for every role in your operation',
    subtitle: 'From warehouse associates to CDL drivers, our AI understands the certifications, experience, and skills that matter in logistics.',
    ctaText: 'Start Free Trial',
    ctaHref: '/onboarding?industry=logistics',
    roles: [
        {
            title: 'Warehouse Associates',
            image: '/assets/landing/logistics/trusted_1.png',
            imageAlt: 'Warehouse associate',
        },
        {
            title: 'Forklift Operators',
            image: '/assets/landing/logistics/trusted_2.png',
            imageAlt: 'Forklift operator',
        },
        {
            title: 'CDL Drivers',
            image: '/assets/landing/logistics/trusted_3.png',
            imageAlt: 'Truck driver',
        },
        {
            title: 'Shipping & Receiving',
            image: '/assets/landing/logistics/trusted_4.png',
            imageAlt: 'Shipping clerk',
        },
        {
            title: 'Inventory Specialists',
            image: '/assets/landing/logistics/trusted_5.png',
            imageAlt: 'Inventory specialist',
        },
    ],
};
// ===== FEATURES SECTION =====
export const features = {
    label: 'WHY LOGISTICS MANAGERS CHOOSE US',
    title: 'Features',
    subtitle: 'Let Stanorfind, screen, and schedule your logistics staff automatically.',
    features: [
        {
            eyebrow: 'STANOR SCORES',
            title: 'Candidate Application/\nResume Scoring',
            description: 'Never read an unqualified resume again—save hours of manual review and instantly focus only on candidates who meet your standards.',
            bullets: [
                'Instant candidates ranking',
                'Smart requirement matching',
                'Zero manual reading',
            ],
            image: '/assets/landing/shared/feature_1.png',
            imageAlt: 'Candidate scoring dashboard',
        },
        {
            eyebrow: 'STANOR QUALIFIES',
            title: 'Automated SMS-First\nScreening',
            description: [
                'Eliminate phone tag, reach workers where they actually respond, and engage the best talent immediately.',
                'The AI instantly texts qualified candidates to verify crucial "hard facts" like commute distance and shift availability.',
            ],
            bullets: [
                'Instant text outreach',
                'Dealbreaker verification',
                'Eliminate phone tag',
            ],
            image: '/assets/landing/shared/feature_2.png',
            imageAlt: 'SMS screening conversation',
        },
        {
            eyebrow: 'STANOR SCHEDULES',
            title: 'Zero-Touch Interview\nScheduling',
            description: [
                'Wake up to a calendar full of interviews, completely eliminating the scheduling headache.',
                'Stanorautomatically books top-matched candidates directly into your calendar and sends proactive text reminders.',
            ],
            bullets: [
                'Smart availability matching',
                'Self-serve candidate booking',
                'Zero back-and-forth',
            ],
            image: '/assets/landing/shared/feature_3.png',
            imageAlt: 'Interview scheduling reminder',
        },
        {
            eyebrow: 'STANOR ANTI-GHOSTING',
            title: 'Smart Reminders End\nCandidate Ghosting',
            description: 'Stanor\'s automated SMS sequence sends perfectly timed follow-ups to drastically reduce your no-show rate, so you never waste time on empty chairs again.',
            bullets: [
                'Perfectly timed touchpoints',
                'Frictionless rescheduling',
                'Zero manual follow-ups',
            ],
            image: '/assets/landing/shared/feature_4.png',
            imageAlt: 'Anti-ghosting SMS reminders and interview confirmations',
        },
    ],
};
// ===== PRIVACY SECTION =====
export const privacy = {
    label: 'SECURITY & TRUST',
    title: 'Privacy & Compliance You Can Rely On',
    subtitle: 'With industry-standard encryption, U.S. data hosting, and strict auto-deletion policies, we protect both your business and your candidates.',
    features: [
        {
            id: 1,
            icon: '/assets/landing/shared/icons/general_safety_icon_lock.svg',
            title: 'We process resumes only to screen & rank',
            description: 'Uploaded files are used exclusively to generate ranked shortlists and are never repurposed, sold, or exposed outside your account.',
        },
        {
            id: 2,
            icon: '/assets/landing/shared/icons/general_safety_icon_encryption.svg',
            title: 'Files encrypted',
            description: 'All resumes are protected with enterprise-grade encryption in transit and at rest, ensuring only you control the data.',
        },
        {
            id: 3,
            icon: '/assets/landing/shared/icons/general_safety_icon_cloud_lock.svg',
            title: 'Stored in the U.S.',
            description: 'Data stays within U.S. data centers that follow strict compliance standards, keeping you aligned with local privacy requirements.',
        },
        {
            id: 4,
            icon: '/assets/landing/shared/icons/general_safety_icon_file_shredder.svg',
            title: 'Auto-delete after 30 days',
            description: 'Every file is automatically removed within 30 days, and you can request deletion at any time for added peace of mind.',
        },
    ],
    image: '/assets/landing/shared/security.png',
    imageAlt: 'Privacy and Security',
};
// ===== PRICING SECTION =====
export const pricing = {
    label: 'PRICING',
    title: 'Start free. Scale only when you’re ready.',
    subtitle: 'See it work on your real applicants before you commit. Paid plans replace one week of staffing-agency markup.',
    topBadges: [
        'Free for 25 candidates',
        'No setup fees',
        '30-day money-back on paid',
        'Cancel anytime',
    ],
    promoBanner: {
        title: '🎉 Free trial — 25 AI-scored resumes + SMS to your own phone. No card.',
        subtitle: 'Test StanorAI on your actual resumes today, then upgrade when you’re ready to message real candidates.',
    },
    plans: [
        {
            name: 'Free',
            highlight: 'Test it on your own resumes — and your own phone.',
            description: 'Prove it works on your own applicants before committing anything. No card.',
            price: '$0',
            features: [
                '25 AI-scored resumes (one-time)',
                'Up to 3 active jobs',
                'Send the SMS qualification flow to your own verified phone',
                'Ranked shortlist with transparent reasoning',
                'Mobile-first — works while you walk the floor',
                'No credit card. No demo call. No setup fees.',
            ],
            ctaText: 'Start Free Trial',
            ctaHref: '/onboarding?industry=logistics',
        },
        {
            name: 'Starter',
            highlight: 'Replace one week of temp-agency markup.',
            description: 'For teams hiring 2–3 people a month at SMB volume.',
            price: '$99',
            period: '/month',
            isPopular: true,
            badge: 'Best for SMBs',
            features: [
                '1,500 AI scans / month',
                '300 SMS messages — qualify ~25 candidates, schedule ~8 interviews',
                'Up to 25 active jobs',
                'Up to 3 team members',
                'Cheaper than 1 day of temp-agency markup',
                'Priority email support',
            ],
            guaranteeText: '30-day money-back guarantee',
            ctaText: 'Get Started',
            ctaHref: '/onboarding?upgrade=starter',
        },
        {
            name: 'Advanced',
            highlight: 'Multi-site, peak-season, agency-volume.',
            description: 'For multi-location and peak-season hiring without adding a recruiter.',
            price: '$249',
            period: '/month',
            badge: 'For peak season',
            features: [
                '5,000 AI scans / month — handle Prime-Day-scale surges',
                '1,000 SMS messages — qualify ~80 candidates, interview ~25',
                'Unlimited active jobs across all sites',
                'Up to 10 team seats',
                'Priority support — direct Slack channel',
            ],
            guaranteeText: '30-day money-back guarantee',
            ctaText: 'Get Started',
            ctaHref: '/onboarding?upgrade=advanced',
        },
    ],
    roiHook: 'Warehouse turnover at 45%/yr means re-hiring 200+ workers a year. Cut agency markup on every one of them.',
};
// ===== FAQ SECTION =====
export const faq = {
    title: 'Frequently Asked Questions',
    subtitle: 'Everything logistics employers need to know',
    faqs: [
        {
            question: 'What does Free actually include?',
            answer: '25 AI-scored resumes (one-time, not monthly), up to 3 active jobs, and the ability to send our SMS qualification flow to your own verified phone — so you can experience what your candidates would. No credit card required.',
        },
        {
            question: 'Why can’t I text candidates on the Free plan?',
            answer: 'Free is a true trial sandbox — you text yourself so you can see how the qualification flow feels from the candidate side. To message real candidates, upgrade to Starter or Advanced. This keeps Free fast and free for everyone instead of being abused as a generic SMS tool.',
        },
        {
            question: 'How does pricing compare to a staffing agency?',
            answer: 'A typical staffing agency adds about 30% markup on hourly wages for 90 days — roughly $1,400+ per hourly hire. Starter at $99/mo covers most SMB hiring volume; Advanced at $249/mo covers multi-site and peak-season hiring. Most teams break even on their first direct hire.',
        },
        {
            question: 'How do I upgrade to Starter or Advanced?',
            answer: 'Click “Get Started” on any paid plan to subscribe instantly via Stripe. You can upgrade, downgrade, or cancel anytime from your dashboard. 30-day money-back guarantee on all paid plans.',
        },
        {
            question: 'Does this work for CDL and certified forklift operators?',
            answer: 'Absolutely. StanorAI is specifically designed for logistics hiring, including: CDL Class A/B drivers, certified forklift operators, warehouse associates, shipping & receiving clerks, inventory specialists, and logistics coordinators. Our AI understands DOT requirements, license classifications, and logistics-specific certifications. You can specify exactly what certifications and experience you need, and we\'ll rank candidates accordingly.',
        },
        {
            question: 'Does StanorAI recognize logistics certifications like CDL and forklift licenses?',
            answer: 'Yes! Our AI is specifically trained to identify and prioritize certifications common in logistics, including CDL Class A/B/C licenses, forklift certifications, OSHA safety training, hazmat endorsements, and warehouse management certifications. Candidates with relevant certifications are automatically scored higher.',
        },
        {
            question: 'Can I screen candidates for different logistics roles in one session?',
            answer: 'Absolutely. You can upload resumes for any logistics position – drivers, warehouse workers, shipping clerks, inventory managers, and more. Just describe what you\'re looking for in the job requirements, and our AI will match candidates accordingly.',
        },
        {
            question: 'How does StanorAI handle candidates with no resume, just an Indeed profile?',
            answer: 'We work with whatever you have. Export candidate profiles from Indeed, ZipRecruiter, or job boards as PDFs and upload them directly. Our AI extracts work history, certifications, and contact information from any standard format.',
        },
        {
            question: 'Is StanorAI accurate for high-turnover warehouse positions?',
            answer: 'StanorAI is designed for exactly this scenario. Our scoring focuses on practical signals like relevant experience duration, skill match, and availability – not just credentials. You\'ll quickly identify candidates who are likely to stay and perform, even among high-volume applicants.',
        },
        {
            question: 'How fast can I screen 100 resumes?',
            answer: 'With our Standard or Pro plan, you can screen 100 resumes in about 3-5 minutes. Upload in bulk, let our AI analyze each candidate against your requirements, and export a ranked list with scores and reasoning for each candidate.',
        },
        {
            question: 'Do I need to integrate with my existing ATS or WMS system?',
            answer: 'No integration required. StanorAI works standalone – simply upload resumes and export your shortlist to Excel or CSV. This makes it perfect for warehouses and distribution centers that don\'t use traditional ATS systems or need quick results without IT involvement.',
        },
        {
            question: 'Can StanorAI filter by shift availability and driving range?',
            answer: 'Yes. If availability or location information is on the resume or application, our AI will extract and consider it. You can also specify required shifts (nights, weekends) or maximum commute distance in your job requirements.',
        },
        {
            question: 'Is there a free trial for logistics employers?',
            answer: 'Yes! Your first 25 resumes are completely free – no credit card required. Perfect for testing with your next batch of applicants. After that, Starter is $99/month.',
        },
    ],
    defaultExpanded: 0,
};
// ===== FOOTER =====
export const footer = {
    tagline: 'AI-powered resume screening built for logistics hiring.',
    columns: [
        {
            title: 'Industries',
            links: [
                { label: 'Logistics', href: '/logistics', isActive: true },
                { label: 'Hospitality', href: '/hospitality' },
                { label: 'All Industries', href: '/' },
            ],
        },
        {
            title: 'Legal',
            links: [
                { label: 'Privacy Policy', href: '/privacy' },
                { label: 'Terms of Service', href: '/terms' },
            ],
        },
    ],
    contact: {
        address1: '522 W Riverside AVE STE N',
        address2: 'Spokane WA 99201',
        email: 'info@stanorai.com',
    },
};
